import {
  getCampaignEndDateMin,
  getCampaignPeriodDateErrors,
  getCampaignStartDateBounds,
} from '@/entities/campaign';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';

type CampaignStartDateErrorCode = NonNullable<
  ReturnType<typeof getCampaignPeriodDateErrors>['startDate']
>;
type CampaignEndDateErrorCode = NonNullable<
  ReturnType<typeof getCampaignPeriodDateErrors>['endDate']
>;

type UseCampaignBasicsPeriodArgs = {
  campaignEndDate: DateISOString | undefined;
  campaignName: string;
  campaignStartDate: DateISOString;
  hasEndDate: boolean;
  setCampaignEndDate: Dispatch<SetStateAction<DateISOString | undefined>>;
  setHasEndDate: Dispatch<SetStateAction<boolean>>;
};

function getCampaignStartDateErrorMessage(errorCode?: CampaignStartDateErrorCode) {
  switch (errorCode) {
    case 'required':
      return 'Choose a start date.';
    case 'out-of-range':
      return 'Start date must be today or later.';
    default:
      return undefined;
  }
}

function getCampaignEndDateErrorMessage(errorCode?: CampaignEndDateErrorCode) {
  switch (errorCode) {
    case 'required':
      return 'Choose an end date.';
    case 'before-start':
      return 'End date must be at least one day after the start date and tomorrow or later.';
    default:
      return undefined;
  }
}

export function useCampaignBasicsPeriod({
  campaignEndDate,
  campaignName,
  campaignStartDate,
  hasEndDate,
  setCampaignEndDate,
  setHasEndDate,
}: UseCampaignBasicsPeriodArgs) {
  const { workspace } = useWorkspace();
  const workspaceTimezone = workspace?.timezone;
  const { min: startingDateMin, max: startingDateMax } = getCampaignStartDateBounds(
    undefined,
    workspaceTimezone,
  );
  const endingDateMin = getCampaignEndDateMin({
    campaignStartDate,
    workspaceTimezone,
  });
  const dateErrors = getCampaignPeriodDateErrors({
    campaignStartDate,
    hasEndDate,
    campaignEndDate,
    workspaceTimezone,
  });

  const campaignStartDateErrorCode = dateErrors.startDate;
  const campaignEndDateErrorCode = dateErrors.endDate;
  const campaignStartDateError = getCampaignStartDateErrorMessage(campaignStartDateErrorCode);
  const campaignEndDateError = getCampaignEndDateErrorMessage(campaignEndDateErrorCode);

  const handleHasEndDateChange = useCallback(
    (checked: boolean) => {
      setHasEndDate(checked);
      if (!checked) {
        setCampaignEndDate(undefined);
      }
    },
    [setCampaignEndDate, setHasEndDate],
  );

  return {
    campaignEndDateError,
    campaignEndDateErrorCode,
    campaignStartDateError,
    campaignStartDateErrorCode,
    endingDateMin,
    handleHasEndDateChange,
    isReady:
      Boolean(campaignName.trim()) &&
      !campaignStartDateError &&
      (!hasEndDate || !campaignEndDateError),
    startingDateMax,
    startingDateMin,
    workspaceTimezone,
  };
}
